import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { Target, Plus, Copy, Trash2, Edit2, ChevronDown, ChevronUp, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_CRITERIA = {
  job_titles: { weight: 30, required: false, values: [], match_type: 'contains_any' },
  industries: { weight: 20, required: false, values: [], match_type: 'contains_any' },
  company_size: { weight: 15, required: false, min_employees: null, max_employees: null },
  locations: { weight: 15, required: false, values: [], match_type: 'contains_any' },
  keywords: { weight: 10, required: false, values: [], match_type: 'contains_any', search_in: ['headline', 'summary', 'experience'] },
  seniority_level: { weight: 10, required: false, values: [], match_type: 'contains_any' },
};

export default function ICPManager() {
  const { isDark } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', criteria: EMPTY_CRITERIA, exclusions: { job_titles: [], companies: [], domains: [] }, auto_qualify_threshold: 70, auto_disqualify_threshold: 30, is_default: false, tier: '', tier_priority: 0, tier_action: 'card' });
  const [tagInputs, setTagInputs] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/icp`, { credentials: 'include' });
      if (res.ok) setTemplates(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    const url = editingId ? `${BACKEND_URL}/api/prospecting/icp/${editingId}` : `${BACKEND_URL}/api/prospecting/icp`;
    const method = editingId ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
      if (res.ok) { fetchTemplates(); setShowForm(false); setEditingId(null); resetForm(); }
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ICP template?')) return;
    await fetch(`${BACKEND_URL}/api/prospecting/icp/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchTemplates();
  };

  const handleDuplicate = async (id) => {
    await fetch(`${BACKEND_URL}/api/prospecting/icp/${id}/duplicate`, { method: 'POST', credentials: 'include' });
    fetchTemplates();
  };

  const startEdit = (t) => {
    setForm({ name: t.name, description: t.description || '', criteria: t.criteria || EMPTY_CRITERIA, exclusions: t.exclusions || { job_titles: [], companies: [], domains: [] }, auto_qualify_threshold: t.auto_qualify_threshold || 70, auto_disqualify_threshold: t.auto_disqualify_threshold || 30, is_default: t.is_default || false, tier: t.tier || '', tier_priority: t.tier_priority || 0, tier_action: t.tier_action || 'card' });
    setEditingId(t.icp_id);
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({ name: '', description: '', criteria: EMPTY_CRITERIA, exclusions: { job_titles: [], companies: [], domains: [] }, auto_qualify_threshold: 70, auto_disqualify_threshold: 30, is_default: false, tier: '', tier_priority: 0, tier_action: 'card' });
    setTagInputs({});
  };

  const updateCriterionValues = (criterionKey, values) => {
    setForm(prev => ({ ...prev, criteria: { ...prev.criteria, [criterionKey]: { ...prev.criteria[criterionKey], values } } }));
  };

  const addTag = (criterionKey, value) => {
    if (!value.trim()) return;
    const current = form.criteria[criterionKey]?.values || [];
    if (!current.includes(value.trim())) {
      updateCriterionValues(criterionKey, [...current, value.trim()]);
    }
    setTagInputs(prev => ({ ...prev, [criterionKey]: '' }));
  };

  const removeTag = (criterionKey, idx) => {
    const current = form.criteria[criterionKey]?.values || [];
    updateCriterionValues(criterionKey, current.filter((_, i) => i !== idx));
  };

  const renderTagInput = (criterionKey, label) => (
    <div>
      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(form.criteria[criterionKey]?.values || []).map((v, i) => (
          <span key={`tag-${v}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
            {v}
            <button onClick={() => removeTag(criterionKey, i)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <input
        value={tagInputs[criterionKey] || ''}
        onChange={e => setTagInputs(prev => ({ ...prev, [criterionKey]: e.target.value }))}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(criterionKey, e.target.value); } }}
        placeholder={`Type and press Enter`}
        className={`w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
      />
      <div className="flex items-center gap-4 mt-2">
        <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Weight: <input type="number" min="0" max="100" value={form.criteria[criterionKey]?.weight || 0} onChange={e => setForm(prev => ({ ...prev, criteria: { ...prev.criteria, [criterionKey]: { ...prev.criteria[criterionKey], weight: parseInt(e.target.value) || 0 } } }))} className={`w-16 ml-1 px-2 py-1 rounded text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300'} border`} />
        </label>
      </div>
    </div>
  );

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;
  const input = `w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} border`;

  return (
    <AppLayout title="ICP Manager">
      <div className="space-y-6" data-testid="icp-manager">
        <ProspectingNav />
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Ideal Customer Profiles</h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Define scoring criteria for lead qualification</p>
          </div>
          <button onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }} data-testid="create-icp-btn" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] transition-colors">
            <Plus className="w-4 h-4" /> New ICP
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className={`w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl p-6 ${isDark ? 'bg-[#111] border border-white/10' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-5">
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editingId ? 'Edit ICP' : 'New ICP Template'}</h2>
                <button onClick={() => { setShowForm(false); setEditingId(null); }} className={`p-1 rounded ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><X className="w-5 h-5" /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Name *</label>
                  <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={input} placeholder="e.g., Enterprise CTO - Cybersecurity" data-testid="icp-name-input" />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                  <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className={`${input} h-20 resize-none`} placeholder="Describe this ICP..." />
                </div>

                <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-4`}>
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Scoring Criteria</h3>
                  <div className="space-y-4">
                    {renderTagInput("job_titles", "Job Titles")}
                    {renderTagInput("industries", "Industries")}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Company Size</label>
                      <div className="flex gap-3">
                        <input type="number" placeholder="Min employees" value={form.criteria.company_size?.min_employees || ''} onChange={e => setForm(p => ({ ...p, criteria: { ...p.criteria, company_size: { ...p.criteria.company_size, min_employees: parseInt(e.target.value) || null } } }))} className={`${input} flex-1`} />
                        <input type="number" placeholder="Max employees" value={form.criteria.company_size?.max_employees || ''} onChange={e => setForm(p => ({ ...p, criteria: { ...p.criteria, company_size: { ...p.criteria.company_size, max_employees: parseInt(e.target.value) || null } } }))} className={`${input} flex-1`} />
                      </div>
                      <label className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Weight: <input type="number" min="0" max="100" value={form.criteria.company_size?.weight || 0} onChange={e => setForm(p => ({ ...p, criteria: { ...p.criteria, company_size: { ...p.criteria.company_size, weight: parseInt(e.target.value) || 0 } } }))} className={`w-16 ml-1 px-2 py-1 rounded text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300'} border`} />
                      </label>
                    </div>
                    {renderTagInput("locations", "Locations")}
                    {renderTagInput("keywords", "Keywords")}
                    {renderTagInput("seniority_level", "Seniority Level")}
                  </div>
                </div>

                <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-4`}>
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Thresholds</h3>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Auto-qualify above</label>
                      <input type="number" min="0" max="100" value={form.auto_qualify_threshold} onChange={e => setForm(p => ({ ...p, auto_qualify_threshold: parseInt(e.target.value) || 0 }))} className={input} />
                    </div>
                    <div className="flex-1">
                      <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Auto-disqualify below</label>
                      <input type="number" min="0" max="100" value={form.auto_disqualify_threshold} onChange={e => setForm(p => ({ ...p, auto_disqualify_threshold: parseInt(e.target.value) || 0 }))} className={input} />
                    </div>
                  </div>
                </div>

                <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-4`}>
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Membership Tier (Multi-Tier Qualification)</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Tier</label>
                      <select value={form.tier} onChange={e => setForm(p => ({ ...p, tier: e.target.value }))} className={input}>
                        <option value="">-- No tier --</option>
                        <option value="professional">Professional ($149)</option>
                        <option value="enterprise">Enterprise</option>
                        <option value="expert">Expert</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Priority (1=first)</label>
                      <input type="number" min="0" max="10" value={form.tier_priority} onChange={e => setForm(p => ({ ...p, tier_priority: parseInt(e.target.value) || 0 }))} className={input} />
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Action</label>
                      <select value={form.tier_action} onChange={e => setForm(p => ({ ...p, tier_action: e.target.value }))} className={input}>
                        <option value="card">Create CRM Card</option>
                        <option value="message">Send Discount Code</option>
                      </select>
                    </div>
                  </div>
                  <p className={`text-xs mt-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Leads are scored bottom-up: Professional (1) → Enterprise (2) → Expert (3). Highest qualifying tier wins.</p>
                </div>

                <label className={`flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <input type="checkbox" checked={form.is_default} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))} className="rounded" />
                  <span className="text-sm">Set as default ICP</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => { setShowForm(false); setEditingId(null); }} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button onClick={handleSave} data-testid="save-icp-btn" className="px-4 py-2 rounded-lg text-sm bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600]">
                  {editingId ? 'Update' : 'Create'} ICP
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Template Cards */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A2FF00]"></div></div>
        ) : templates.length === 0 ? (
          <div className={`${card} p-12 text-center`}>
            <Target className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No ICP templates yet. Create your first one to start qualifying leads.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {templates.map(t => (
              <div key={t.icp_id} className={card} data-testid={`icp-card-${t.icp_id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.name}</h3>
                        {t.is_default && <span className="px-2 py-0.5 bg-[#A2FF00]/20 text-[#A2FF00] rounded text-xs font-medium">Default</span>}
                        {t.tier && <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.tier === 'expert' ? 'bg-purple-500/20 text-purple-400' : t.tier === 'enterprise' ? 'bg-blue-500/20 text-blue-400' : 'bg-teal-500/20 text-teal-400'}`}>{t.tier}</span>}
                      </div>
                      {t.description && <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.description}</p>}
                      <div className={`flex gap-4 mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <span>Scored: {t.leads_scored || 0} leads</span>
                        <span>Avg Score: {t.avg_score ? t.avg_score.toFixed(1) : '0'}</span>
                        <span>Qualify: &ge;{t.auto_qualify_threshold}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setExpandedId(expandedId === t.icp_id ? null : t.icp_id)} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                        {expandedId === t.icp_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <button onClick={() => startEdit(t)} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`} data-testid={`edit-icp-${t.icp_id}`}><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDuplicate(t.icp_id)} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><Copy className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(t.icp_id)} className={`p-2 rounded-lg hover:bg-red-500/20 text-red-400`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>

                  {expandedId === t.icp_id && (
                    <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        {t.criteria?.job_titles?.values?.length > 0 && (
                          <div>
                            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Job Titles (w:{t.criteria.job_titles.weight})</span>
                            <div className="flex flex-wrap gap-1 mt-1">{t.criteria.job_titles.values.map((v) => <span key={v} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{v}</span>)}</div>
                          </div>
                        )}
                        {t.criteria?.industries?.values?.length > 0 && (
                          <div>
                            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Industries (w:{t.criteria.industries.weight})</span>
                            <div className="flex flex-wrap gap-1 mt-1">{t.criteria.industries.values.map((v) => <span key={v} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{v}</span>)}</div>
                          </div>
                        )}
                        {t.criteria?.locations?.values?.length > 0 && (
                          <div>
                            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Locations (w:{t.criteria.locations.weight})</span>
                            <div className="flex flex-wrap gap-1 mt-1">{t.criteria.locations.values.map((v) => <span key={v} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{v}</span>)}</div>
                          </div>
                        )}
                        {t.criteria?.keywords?.values?.length > 0 && (
                          <div>
                            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Keywords (w:{t.criteria.keywords.weight})</span>
                            <div className="flex flex-wrap gap-1 mt-1">{t.criteria.keywords.values.map((v) => <span key={v} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{v}</span>)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
